/**
 * One Client (f6.1) -- and the **tab container** three later phases open.
 *
 * The plan calls the later tabs *"intended rework"*. Rework is cheap only if
 * the shell is a host, so it is one from the first commit: the strip and the
 * panels are rendered from a **declared array** of tab descriptors, and f7.1
 * (`Engagements`), f11.4 (`Commitments`) and f15.1 (`Artifacts`) each add ONE
 * entry. Nothing else here changes when they do -- no restructuring, and no
 * tab registry, plugin seam or lazy-loading machinery either. The cost of the
 * host is the array; that is the whole anticipation budget.
 *
 * **f7.1 proved it.** It added its entry and its panel and changed nothing
 * else in this file but the import and the array -- which is the plan's sixth
 * acceptance criterion exercised for real rather than by a test double. It
 * also PINNED the entry shape f6.1 left open, so f11.4 and f15.1 inherit it.
 *
 * The two remaining tabs are **absent, not disabled**, per f4.1's and f5.1's
 * rule: a disabled tab advertises a feature that does not exist and has to be
 * un-disabled, while an absent one costs one array entry to add. The
 * `Engagements` count badge was absent rather than zero until f7.1 supplied a
 * real `engagement_count` -- a zero would have been a claim.
 *
 * **No CSS is written.** `.tabs`, `.tab`, `.tab.is-active`, `.tab .count` and
 * `.tab:focus-visible` were ported verbatim into `styles/index.css` when f4.1
 * built its own single-tab strip, including the `background: none` that lets a
 * tab be a `<button>` rather than the prototype's anchor. Accessibility
 * follows the shipped precedent in `BusinessUnitGroupDetail.tsx`.
 *
 * **What the prototype draws that is deliberately absent:**
 *
 * | Prototype element | Why | Owner |
 * | --- | --- | --- |
 * | The four stat cards -- `Budget 2026`, `Mid Aug 2026 COM`, `Unweighted`, `Risk to COM` | Every one is per-cycle data, and no cycle exists | f10.x-f14.x, budget via adr-10 |
 * | `Review notes · cycle 2607` and its `Open review` link | Per-cycle client review | f12.1 |
 * | `Commitment history` chart | Per-cycle history; Recharts is chosen but there is nothing to plot | f16.1, adr-6 |
 * | `New engagement` in the page header | No target entity exists | f7.1 |
 *
 * The `Ownership` card is **f4.2's component, reused** and extended here with
 * the inherited rows the prototype draws -- the DEM and CEM of the group and
 * the CM of the unit, read-only and naming the node each comes from, beside
 * the client's own DM badged `Owner`. That chain is **display only**: it is
 * one query for a screen, it computes no permission, and it is not the scope
 * walk-down BR-CALC-022 defines, which f9.1 owns.
 */

import { type ReactNode } from 'react';
import {
  ArrowLeft,
  FileSignature,
  FileText,
  Info,
  TrendingUp,
} from 'lucide-react';
import { Link, useParams, useSearchParams } from 'react-router-dom';

import type { Client } from '@/api/client';
import { useClient, useClientCommitments, useLookupCatalog } from '@/api/queries';
import { OwnershipCard } from '@/ownership/OwnershipCard';
import { CommitmentsPanel } from '@/routes/clients/commitments/CommitmentsPanel';
import { pendingCount } from '@/routes/clients/commitments/commitmentsView';
import { EngagementsPanel } from '@/routes/clients/engagements/EngagementsPanel';
import { DELIVERY_ROLE_TYPE } from '@/routes/business-unit-groups/ownershipView';
import { ArtifactsPanel } from '@/routes/artifacts/ArtifactsPanel';
import { useSessionScope } from '@/session/SessionScopeContext';
import { ASSIGN_OWNER, MANAGE_HIERARCHY, PUBLISH_ARTIFACT, rowCan } from '@/session/permissions';
import { RecordUnavailable } from '@/ui/RecordUnavailable';
import { GroupParentLink, UnitParentLink } from '@/ui/ScopedParentLink';
import { TabHost, type TabDescriptor } from '@/ui/TabHost';

import {
  CLIENT_ROLE_CODES,
  PROVENANCE_MISSING,
  badgeClassFor,
  formatActor,
  formatMoment,
  initialsOf,
} from './clientView';

/** The level this client's own assignments are recorded against. */
const NODE_TYPE = 'CLIENT';

/*
 * The tab host and its descriptor shape moved to `@/ui/TabHost` in f15.1, when
 * the Business Unit Group and Business Unit screens gained a second tab. They
 * are re-exported here so existing imports keep working.
 */
export { TabHost, type TabDescriptor } from '@/ui/TabHost';

/** The search parameter the active tab lives in (f11.4), so Back from the grid lands on it. */
const TAB_PARAM = 'tab';

function BackLink() {
  return (
    <Link className="btn btn-secondary btn-sm" to="/clients">
      <ArrowLeft aria-hidden="true" /> Clients
    </Link>
  );
}

/** One label-and-value pair in the Details tab. */
function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="detail-meta-item">
      <div className="label">{label}</div>
      <div>{children}</div>
    </div>
  );
}

function Provenance({
  label,
  at,
  by,
}: {
  label: string;
  at: string | null | undefined;
  by: { display: string } | null | undefined;
}) {
  const actor = formatActor(by);
  return (
    <Fact label={label}>
      <div>{formatMoment(at)}</div>
      <span className="row group-actor">
        {actor === PROVENANCE_MISSING ? (
          <span className="hint">{actor}</span>
        ) : (
          <>
            <span className="avatar sm" aria-hidden="true">
              {initialsOf(actor)}
            </span>
            {actor}
          </>
        )}
      </span>
    </Fact>
  );
}

function DetailsPanel({ client }: { client: Client }) {
  const catalog = useLookupCatalog();
  const deliveryRoles =
    catalog.data?.types.find((type) => type.code === DELIVERY_ROLE_TYPE)?.values ?? [];

  return (
    <div className="detail-columns">
      <div className="card">
        <div className="card-body">
          <div className="detail-meta">
            <Fact label="Code">
              <span className="mono">{client.code}</span>
            </Fact>
            <Fact label="Name">{client.name}</Fact>
            <Fact label="SAP ID">
              {client.sap_id ? (
                <span className="mono">{client.sap_id}</span>
              ) : (
                /*
                 * The normal case after a master import: the master workbook
                 * carries no SAP ID column at all, so f10.4 fills these in
                 * from the Kimble engagement feed.
                 */
                <span className="hint">Not recorded yet</span>
              )}
            </Fact>
            {/*
              f9.2: plain text, not a link, when the parent is outside the
              caller's scope -- a link to a guaranteed 404 is a worse answer
              than a label. A DM sees their client and not the unit above it.
            */}
            <Fact label="Business Unit">
              <UnitParentLink unit={client.business_unit} />
            </Fact>
            <Fact label="Business Unit Group">
              <GroupParentLink group={client.business_unit_group} />
            </Fact>
            <Fact label="Reporting currency">
              {/*
                INHERITED from the Business Unit and read-only: there is no
                column on the client, because nothing in the estate lets a
                client differ from its unit.
              */}
              <span className="badge badge-chip">{client.reporting_currency.code}</span>{' '}
              {client.reporting_currency.name}
              <span className="hint"> · from {client.business_unit.name}</span>
            </Fact>
            <Fact label="Status">
              <span className={badgeClassFor(client.status.code)}>
                {client.status.name}
              </span>
            </Fact>
            <Fact label="Kimble account aliases">
              {client.aliases.length === 0 ? (
                /*
                 * An empty set is a real answer rather than a gap: a casing
                 * difference needs no alias row at all, because the normaliser
                 * resolves it.
                 */
                <span className="hint">None recorded</span>
              ) : (
                <span className="row alias-chips">
                  {client.aliases.map((alias) => (
                    <span className="badge badge-chip" key={alias}>
                      {alias}
                    </span>
                  ))}
                </span>
              )}
            </Fact>
            <Provenance label="Created" at={client.created_at} by={client.created_by} />
            <Provenance
              label="Last changed"
              at={client.updated_at}
              by={client.updated_by}
            />
          </div>
        </div>
      </div>

      <div className="stack-sm">
        {/*
          f4.2's card, parameterised rather than copied. Its role selector
          offers DM only, because the node-type registry says that is what this
          level supports.
        */}
        <OwnershipCard
          nodeType={NODE_TYPE}
          nodeId={client.id}
          nodeName={client.name}
          roleCodes={CLIENT_ROLE_CODES}
          deliveryRoles={deliveryRoles}
          mayManage={rowCan(client, ASSIGN_OWNER)}
          emptyHint="No Delivery Manager is named on this client yet. Any Atlas account can be assigned here, including one nobody has signed in to yet."
        />
        <InheritedOwnership client={client} />
      </div>
    </div>
  );
}

/**
 * The chain above this client, read-only.
 *
 * The prototype puts all four owners on one card. They are rendered as two
 * here for a reason the prototype could not show: the top three rows carry no
 * `Assign` and no `End`, because the control that changes them lives on the
 * screen that owns them. Splitting the card is what makes "read-only" visible
 * rather than merely true.
 *
 * **It grants nothing.** This is a read that shows who is accountable; f9.1
 * owns the walk-down that decides who may see what.
 */
function InheritedOwnership({ client }: { client: Client }) {
  const inherited = client.inherited_owners ?? [];
  if (inherited.length === 0) return null;

  return (
    <div className="card">
      <div className="card-head">
        <h3>Inherited ownership</h3>
      </div>
      <div className="card-body stack-sm">
        {inherited.map((owner) => (
          <div className="row owner-row" key={owner.assignment_id}>
            <span className="avatar sm" aria-hidden="true">
              {initialsOf(owner.display_name)}
            </span>
            <div className="owner-identity">
              <div className="user-name">{owner.display_name}</div>
              <div className="hint">
                {owner.delivery_role_code} &middot; {owner.node_label}
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className="card-foot">
        <span className="hint">
          Named above this client. Change them where they are owned.
        </span>
      </div>
    </div>
  );
}

export function ClientDetailPage() {
  const { clientId = '' } = useParams();
  const { can } = useSessionScope();
  const [params, setParams] = useSearchParams();
  /*
   * f9.2: the route guard in `App.tsx` reads the navigation key. The request
   * is always ISSUED for the id, and the state is chosen from the response --
   * the client never pre-checks an id against anything.
   */
  const detail = useClient(clientId, { enabled: Boolean(clientId) });
  /*
   * f11.4: the SAME query the Commitments panel reads (one key, one fetch) --
   * here only for the tab badge, the number of cycles with input pending.
   */
  const commitments = useClientCommitments(clientId, { enabled: Boolean(detail.data) });

  if (detail.isPending) {
    return (
      <>
        <div className="page-head">
          <div>
            <h1 className="page-title">Client</h1>
          </div>
          <div className="page-actions">
            <BackLink />
          </div>
        </div>
        <div className="card" aria-busy="true" aria-label="Loading the Client">
          <div className="card-body">
            {[0, 1, 2, 3].map((index) => (
              <span key={index} className="skeleton-bar group-skeleton-line" />
            ))}
          </div>
        </div>
      </>
    );
  }

  if (detail.error || !detail.data) {
    /*
     * One shared state for all four levels. A 404 does not say whether the
     * client is missing or merely not the caller's (Q-f9.1-3).
     */
    return (
      <RecordUnavailable
        error={detail.error}
        backTo="/clients"
        backLabel="Clients"
        onRetry={() => void detail.refetch()}
      />
    );
  }

  const client = detail.data;
  const mayManage = can(MANAGE_HIERARCHY);

  /*
   * The tab array. `Engagements` (f7.1), `Commitments` (f11.4) and `Artifacts`
   * (f15.1) are ONE entry each, added here and nowhere else.
   *
   * f7.1's entry is the first, and it is what exercises the plan's sixth
   * acceptance criterion for f6.1 for REAL rather than by a test double: this
   * is the first genuine second tab, and adding it restructured nothing.
   *
   * `count` is the server's `engagement_count` -- ACTIVE engagements, resolved
   * in one query over every client -- so the badge and the Clients list's
   * column can never disagree. It is the first count this strip has rendered;
   * before f7.1 it was absent rather than zero.
   */
  const tabs: TabDescriptor[] = [
    {
      id: 'details',
      label: 'Details',
      icon: <Info aria-hidden="true" />,
      panel: <DetailsPanel client={client} />,
    },
    {
      id: 'engagements',
      label: 'Engagements',
      icon: <FileSignature aria-hidden="true" />,
      count: client.engagement_count,
      panel: <EngagementsPanel client={client} mayManage={mayManage} />,
    },
    /*
     * f11.4: entry route B. The badge counts cycles with input pending for
     * this viewer, and is ABSENT at zero -- f7.1's rule.
     */
    {
      id: 'commitments',
      label: 'Commitments',
      icon: <TrendingUp aria-hidden="true" />,
      count: pendingCount(commitments.data),
      panel: <CommitmentsPanel client={client} />,
    },
    /*
     * f15.1: artifacts owned by the client AND by its engagements (Q-f15.1-4),
     * which is what `artifact_count` counts.
     */
    {
      id: 'artifacts',
      label: 'Artifacts',
      icon: <FileText aria-hidden="true" />,
      count: client.artifact_count,
      panel: (
        <ArtifactsPanel
          ownerType="CLIENT"
          ownerId={client.id}
          ownerName={client.name}
          mayPublish={rowCan(client, PUBLISH_ARTIFACT)}
          clientId={client.id}
        />
      ),
    },
  ];

  return (
    <>
      <div className="page-head">
        <div>
          <div className="header-crumb">
            <Link to="/clients">Clients</Link> <span>/</span> <b>{client.name}</b>
          </div>
          <h1 className="page-title">{client.name}</h1>
          <div className="page-sub">
            <span className="mono">{client.code}</span>
            {client.sap_id ? ` · SAP ${client.sap_id}` : ''} ·{' '}
            {client.business_unit.name} · {client.business_unit_group.name}
          </div>
        </div>
        <div className="page-actions">
          <span className={badgeClassFor(client.status.code)}>{client.status.name}</span>
          <BackLink />
        </div>
      </div>

      <TabHost
        label="Client"
        tabs={tabs}
        activeId={params.get(TAB_PARAM)}
        onSelect={(id) =>
          setParams(
            (current) => {
              const next = new URLSearchParams(current);
              next.set(TAB_PARAM, id);
              return next;
            },
            { replace: true },
          )
        }
      />
    </>
  );
}
